import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ShieldCheck, Radio, Eye, Users, ScrollText, CalendarClock, HeartPulse } from "lucide-react";
import api from "../lib/api";
import GuardianShare from "../components/guardian/GuardianShare";
import GuardianWatch from "../components/guardian/GuardianWatch";
import GuardianCircles from "../components/guardian/GuardianCircles";
import GuardianCheckins from "../components/guardian/GuardianCheckins";
import GuardianEmergencyCard from "../components/guardian/GuardianEmergencyCard";
import GuardianSOS from "../components/guardian/GuardianSOS";
import GuardianGroup from "../components/guardian/GuardianGroup";
import { toast } from "sonner";

/* Travel Guardian — consent-based location, safety & family coordination.
   Slice 1: circles + invites, sessions (3 modes), precision/delay, live map,
   one-tap statuses, access log. */

const TABS = [
  ["share", "My Sharing", Radio],
  ["watch", "My Travelers", Eye],
  ["group", "Group", Users],
  ["checkins", "Check-Ins", CalendarClock],
  ["circles", "Circles", Users],
  ["card", "Emergency Card", HeartPulse],
  ["log", "Access Log", ScrollText],
];

export default function TravelGuardian() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "share";
  const [ov, setOv] = useState(null);

  useEffect(() => {
    document.title = "Travel Guardian — My Travel Hub | TravelerPulse";
    api.get("/guardian/overview").then((r) => setOv(r.data)).catch(() => {});
  }, [tab]);

  return (
    <div className="max-w-5xl mx-auto px-5 py-8" data-testid="guardian-page">
      <div className="flex items-center gap-3">
        <span className="inline-flex p-2.5 rounded-2xl bg-gradient-to-br from-emerald-600 to-teal-600 text-white shadow-lg"><ShieldCheck className="w-6 h-6" /></span>
        <div>
          <h1 className="font-heading font-900 text-2xl sm:text-3xl tracking-tight" data-testid="guardian-title">Travel Guardian</h1>
          <p className="text-xs text-zinc-500">Travel freely. Keep the people you trust informed.</p>
        </div>
        {(ov?.active_sessions || 0) > 0 && (
          <span className="ml-auto inline-flex items-center gap-1.5 bg-emerald-100 text-emerald-800 border border-emerald-300 rounded-full px-3 py-1 text-xs font-800 animate-pulse" data-testid="guardian-active-indicator">
            <Radio className="w-3.5 h-3.5" /> SHARING ACTIVE
          </span>
        )}
      </div>

      <p className="mt-3 text-xs text-zinc-500 border border-zinc-200 bg-zinc-50 rounded-xl px-3 py-2" data-testid="guardian-consent-note">
        All sharing here is <b>voluntary, transparent, time-limited and revocable</b>. Only people you invite — and accept — can see anything, and only at the precision you choose. You can stop instantly, any time.
      </p>

      <div className="mt-4" data-testid="guardian-sos-area">
        <GuardianSOS />
      </div>

      <div className="mt-5 flex gap-1 border-b border-zinc-200 overflow-x-auto" data-testid="guardian-tabs">
        {TABS.map(([id, label, Icon]) => (
          <button key={id} onClick={() => setParams({ tab: id })} data-testid={`guardian-tab-${id}`}
            className={`shrink-0 inline-flex items-center gap-1.5 px-4 py-2.5 text-sm font-700 border-b-2 -mb-px transition-colors ${tab === id ? "border-emerald-600 text-emerald-700" : "border-transparent text-zinc-500 hover:text-zinc-800"}`}>
            <Icon className="w-4 h-4" /> {label}
          </button>
        ))}
      </div>

      <div className="mt-6">
        {tab === "share" && <GuardianShare />}
        {tab === "watch" && <GuardianWatch />}
        {tab === "group" && <GuardianGroup />}
        {tab === "checkins" && <GuardianCheckins />}
        {tab === "circles" && <GuardianCircles />}
        {tab === "card" && <GuardianEmergencyCard />}
        {tab === "log" && <><AccessLog /><SafetyExit /></>}
      </div>
    </div>
  );
}

function SafetyExit() {
  const [confirm, setConfirm] = useState(false);
  const [result, setResult] = useState(null);
  const run = async () => {
    try {
      const r = await api.post("/guardian/safety-exit");
      setResult(r.data); setConfirm(false);
      toast.success("Safety exit complete — all sharing stopped, all viewers revoked");
    } catch { toast.error("Safety exit failed — try again"); }
  };
  return (
    <div className="mt-8 border border-rose-200 bg-rose-50/40 rounded-2xl p-4" data-testid="guardian-safety-exit">
      <h3 className="font-heading font-800 text-sm text-rose-800">Safety Exit</h3>
      <p className="mt-1 text-xs text-zinc-600">If you ever feel unsafe or watched: one tap stops every active session, revokes <b>every</b> viewer and voids all pending invitations — instantly and silently. Revoked viewers are never notified. Your access logs are preserved as evidence.</p>
      {result ? (
        <div className="mt-3 text-xs text-zinc-700" data-testid="guardian-safety-exit-result">
          <p className="font-800 text-emerald-700">Done: {result.sessions_stopped} session(s) stopped, {result.members_revoked} viewer(s) revoked, {result.checkins_cancelled} check-in(s) cancelled.</p>
          <ul className="mt-2 list-disc pl-4 space-y-0.5 text-zinc-600">{result.next_steps.map((s) => <li key={s}>{s}</li>)}</ul>
        </div>
      ) : !confirm ? (
        <button onClick={() => setConfirm(true)} data-testid="guardian-safety-exit-btn"
          className="mt-3 border border-rose-400 text-rose-700 text-xs font-900 rounded-full px-5 py-2.5 hover:bg-rose-100">SAFETY EXIT — STOP & REVOKE EVERYTHING</button>
      ) : (
        <div className="mt-3 flex flex-wrap gap-2 items-center">
          <span className="text-xs font-800 text-rose-800">This revokes every viewer across all your circles. Sure?</span>
          <button onClick={run} data-testid="guardian-safety-exit-confirm" className="bg-rose-600 text-white text-xs font-900 rounded-full px-4 py-2 hover:bg-rose-700">Yes — do it now</button>
          <button onClick={() => setConfirm(false)} className="text-xs text-zinc-500 px-2">Cancel</button>
        </div>
      )}
    </div>
  );
}

function AccessLog() {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/guardian/access-log").then((r) => setItems(r.data.items)).catch(() => setItems([])); }, []);
  if (!items) return <p className="text-sm text-zinc-400">Loading…</p>;
  return (
    <div data-testid="guardian-access-log">
      <p className="text-xs text-zinc-500 mb-3">Every time someone views your location or status, it's recorded here. Transparency works both ways.</p>
      {items.length === 0 ? (
        <p className="text-sm text-zinc-400 border border-dashed border-zinc-300 rounded-xl px-4 py-8 text-center">No one has viewed your location yet.</p>
      ) : items.map((l) => (
        <div key={l.id} className="flex items-center gap-3 border-b border-zinc-100 py-2.5 text-sm" data-testid="guardian-access-log-row">
          <Eye className="w-4 h-4 text-violet-500 shrink-0" />
          <span className="font-700">{l.viewer_name || "A viewer"}</span>
          <span className="text-zinc-500 text-xs">viewed your Guardian status</span>
          <span className="ml-auto text-xs text-zinc-400">{new Date(l.viewed_at).toLocaleString()}</span>
        </div>
      ))}
    </div>
  );
}
